import { Box, Grid, Stack, styled } from '@mui/material'
import { ReactNode } from 'react'
import { useAction, getTimeDifference } from '../../containers/reservationAcceptability/reservationAcceptabilityService'
import { translate } from '../../i18n'
import { ButtonL } from '../components/common/buttons/buttonL'
import { GContainer, GItem, LabelGItemBase, RowSpacerGItem, ValueGItemBase } from '../components/common/grids'
import { InfoCard } from '../components/common/infoCard'
import { formatHmtoHmOver, formatYmdWeek, fromApiYmdHms, getNow } from '../../utils/dateUtil'
import { BottomButtonGroup } from '../components/common/bottomButtonGroup'
import { ButtonOutlinedWarningL } from '../components/common/buttons/buttonOutlinedWarningL'
import { CountdownTimer } from '../components/reservationAcceptability/countdownTimer'
import { differenceInMilliseconds } from 'date-fns'
import { Dialog } from '../components/common/dialog'
import { ButtonOutlinedS } from '../components/common/buttons/buttonOutlinedS'
import { reservationStatus } from '../../containers/common/constant/classification'

const LabelGItem = (props: { children?: ReactNode }) => <LabelGItemBase xs={5}>{props.children}</LabelGItemBase>
const ValueGItem = (props: { children: ReactNode }) => <ValueGItemBase xs={7}>{props.children}</ValueGItemBase>

// フラグ項目設定値
const FLG_ON = '1'

interface AlertCardProps {
  children: ReactNode
  className?: string
}

const Root = styled('div')(({ theme }) => ({
  fontSize: theme.typography.font.sizeL1,
  borderRadius: 4,
  color: theme.palette.error.main,
  backgroundColor: theme.palette.errorPale.main,
  width: '100%',
  padding: '0px',
}))

const Root2 = styled('div')(({ theme }) => ({
  fontSize: theme.typography.font.sizeL1,
  borderRadius: 4,
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.white.main,
  width: '100%',
  padding: '0px',
}))

const AlertCard = (props: AlertCardProps) => {
  return <Root className={props.className}>{props.children}</Root>
}

const AlertCard2 = (props: AlertCardProps) => {
  return <Root2 className={props.className}>{props.children}</Root2>
}

const DialogContentText = styled('div')({
  whiteSpace: 'pre-line',
  alignItems: 'center',
})

const ButtonBox = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '8px',
  gap: '8px',
})

const BaseElementButtonBox  = styled('div')({
  display: 'flex',
  justifyContent: 'center',
})

export const ReservationAcceptability = () => {
  const {
    reservationAcceptability,
    goChange,
    goCancel,
    putReservationAcceptResultCode,
    dialog,
  } = useAction()

  if (reservationAcceptability == null) {
    return null
  }
  
  // キャンセル時自動通知機能フラグが利用するか判定する（利用するの場合『true』）
  const isCancelAutoSendingFlag = reservationAcceptability.cancelAutoSendingFlag === FLG_ON

  // キャンセル時自動通知説明文の登録内容を格納
  const cancelAutoSendingInformation = reservationAcceptability.cancelAutoSendingInformation == null ? '': reservationAcceptability.cancelAutoSendingInformation 	
  const isCancelAutoSendingInformationExist = cancelAutoSendingInformation.length > 0  	

  // 利用予約情報ステータスがキャンセル待ちか判定する（キャンセル待ちの場合『true』）
  const isWaitStatus = reservationAcceptability.status === reservationStatus.wait

  // アクティブ連番が取得できたか判定する（アクティブ連番が取得できたら『true』）
  const isActiveSeq = reservationAcceptability.activeSeq != null

  // 締切日時が過ぎているかを判定する（締切日時が過ぎたら『true』）
  const isDeadlineTime = differenceInMilliseconds(fromApiYmdHms(reservationAcceptability.closeDatetime), getNow()) < 0

  let remainingTime: { hours:number; minutes:number; seconds:number;} = { hours: 0, minutes: 0, seconds: 0 }
  if ((isDeadlineTime) || (!isActiveSeq)) {
    /**
     *  アクティブ連番が取得できない（次のキャンセル待ち希望者へ順番が移っている状態）か、
     *  締切日時が過ぎている場合は、残り時間を『0』に設定し、メッセージ表示につなげる
     */
  } else {
    remainingTime = getTimeDifference(fromApiYmdHms(reservationAcceptability.closeDatetime), getNow())
  }
  
  // 『予約を確定』『キャンセル待ち取消』処理が正常終了したかを判定する（正常終了で『true』）
  const isExecSuccess = putReservationAcceptResultCode === 0

  return (
    <Stack spacing={2}>

      <Dialog
        isOpen={dialog.isOpen}
        onClose={dialog.onClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContentText>{dialog.message}</DialogContentText>
        <BaseElementButtonBox>
          <ButtonBox>
            <ButtonOutlinedS onClick={dialog.onClose}>
              {translate('system.button.close')}
            </ButtonOutlinedS>
          </ButtonBox>
        </BaseElementButtonBox>
      </Dialog>

      <Box>
        <GContainer rowSpacing={2}>
          <GItem md={3}></GItem>
          <GItem xs={12} md={6}>
            <InfoCard>

              {/** 警告メッセージ表示枠（残り時間や注意文言等） */}
              {!isWaitStatus && (
                <div style={{ textAlign: 'center', whiteSpace: 'pre-wrap' }}>
                  <AlertCard>
                    {translate('reservationAcceptability.text.notWaitStatusMessage')}
                  </AlertCard>
                </div>
              )}
              {!isExecSuccess && isWaitStatus && (
                <AlertCard>
                  <CountdownTimer initMinutes={remainingTime.minutes} initSeconds={remainingTime.seconds} />
                </AlertCard>
              )}

              <GContainer>

                <RowSpacerGItem />
              {/** 利用予定日時 */}
                <LabelGItem>{translate('reservationAcceptability.label.usageScheduleDatetime')}</LabelGItem>
                <ValueGItem>
                  <Stack>
                    <div>{formatYmdWeek(reservationAcceptability.usageDate)}</div>
                    <div>
                      {formatHmtoHmOver(
                        { from: reservationAcceptability.useFromDatetime, to: reservationAcceptability.useToDatetime },
                        reservationAcceptability.usageDate
                      )}
                    </div>
                  </Stack>
                </ValueGItem>

                {/** 施設名 */}
                <LabelGItem>{translate('reservationAcceptability.label.facilityName')}</LabelGItem>
                <ValueGItem>{reservationAcceptability.facilityName}</ValueGItem>
                <RowSpacerGItem />
                        
                {/** お子さま */}
                <LabelGItem>{translate('reservationAcceptability.label.childName')}</LabelGItem>
                <ValueGItem>{reservationAcceptability.childName}</ValueGItem>
                <LabelGItem>{translate('reservationAcceptability.label.childNameKana')}</LabelGItem>
                <ValueGItem>{reservationAcceptability.childKana}</ValueGItem>
                <RowSpacerGItem />

                {/** 保護者
                <LabelGItem>{translate('reservationAcceptability.label.userName')}</LabelGItem>
                <ValueGItem>{reservationAcceptability.name}</ValueGItem>
                <LabelGItem>{translate('reservationAcceptability.label.userNameKana')}</LabelGItem>
                <ValueGItem>{reservationAcceptability.kana}</ValueGItem>
                <RowSpacerGItem />
                */}

                {/** 締切時間 */}
                <LabelGItem>{translate('reservationAcceptability.label.closeDatetime')}</LabelGItem>
                <ValueGItem>{reservationAcceptability.closeDatetime}</ValueGItem>

              </GContainer>


            </InfoCard>
          </GItem>
          <GItem md={3}></GItem>
        </GContainer>
      </Box>

      {/** キャンセル時自動通知説明文表示枠 */}
      {isCancelAutoSendingFlag && isCancelAutoSendingInformationExist && (
        <Box>
          <GContainer rowSpacing={2}>
            <GItem md={3}></GItem>
            <GItem xs={12} md={6}>
              <InfoCard>
                  <div style={{ textAlign: 'center', whiteSpace: 'pre-wrap' }}>
                    <AlertCard2>
                      {cancelAutoSendingInformation}
                    </AlertCard2>
                  </div>
              </InfoCard>
            </GItem>
            <GItem md={3}></GItem>
          </GContainer>
        </Box>
      )}


      <BottomButtonGroup>
        {/** キャンセル待ちを取消する */}
        {!isDeadlineTime && !isExecSuccess && isActiveSeq && isWaitStatus && (
          <ButtonOutlinedWarningL fullWidth onClick={goCancel}>
              {translate('reservationAcceptability.button.waitCancel')}
          </ButtonOutlinedWarningL>
        )}
        {/** 予約を確定する */}
        {!isDeadlineTime && !isExecSuccess && isActiveSeq && isWaitStatus && (
          <ButtonL fullWidth onClick={goChange}>
            {translate('reservationAcceptability.button.decision')}
          </ButtonL>
        )}
      </BottomButtonGroup>


      <Grid container>
        <Grid item md={2}></Grid>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <Grid container rowSpacing={1}>
              {/*
              <Grid item xs={12}>
                <Link href={aboutTemporaryCustodyOfInfantsUrl} target="_blank">
                  {translate('home.button.aboutTemporaryCustodyOfInfants')}
                </Link>
              </Grid>
              <Grid item xs={12}>
                <Link href={aboutTemporaryChildcareBusinessUrl} target="_blank">
                  {translate('home.button.aboutTemporaryChildcareBusiness')}
                </Link>
              </Grid>
              <Grid item xs={12}>
                <Link href={aboutFirstChildcareTicketUrl} target="_blank">
                  {translate('home.button.aboutFirstChildcareTicket')}
                </Link>
              </Grid>
              */}
            </Grid>
          </Stack>
        </Grid>
        <Grid item md={2}></Grid>
      </Grid>
    </Stack>
  )
}
